import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedPhoneNumber from 'I18n/components/FormattedPhoneNumber';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import unescapedText from 'I18n/utils/unescapedText';
import { CallingIcon } from 'foundations-assets/icons/components/CallingIcon';
import { EmailIcon } from 'foundations-assets/icons/components/EmailIcon';
import { MessagesIcon } from 'foundations-assets/icons/components/MessagesIcon';
import { SocialBlockFacebookIcon } from 'foundations-assets/icons/components/SocialBlockFacebookIcon';
import { WebsiteIcon } from 'foundations-assets/icons/components/WebsiteIcon';
import { Flex } from 'foundations-components/components/Flex';
import { CopyButton } from 'foundations-components/transitional/components/CopyButton';
import { DescriptionList } from 'foundations-components/transitional/components/DescriptionList';
import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
import { Link } from 'foundations-components/transitional/components/Link';
import { Fragment, useMemo } from 'react';
import { useDetailsPageContext } from 'marketplace-ui-common/context/DetailsPageContext';
import { getFormattedUrl } from 'marketplace-ui-common/utils/DetailsPageUrls';
import { trackCustomMarketplaceOfferingBehavioralEvent } from 'marketplace-ui-common/utils/TrackingUtils';
import { Languages } from './Languages';
import { getUserHasZorseChatScope, getUserHasZorseWebticketScope } from './utils';
// @ts-expect-error JS file
import { openHelpWidget } from './openHelpWidget';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const NoIcon = () => null;
function LinkItem({
  children,
  href,
  onClick
}) {
  return /*#__PURE__*/_jsx(Link, {
    external: true,
    ExternalIcon: NoIcon,
    href: href,
    onClick: onClick,
    children: children
  });
}
export function SupportTab({
  liveChatUrl,
  supportEmail,
  facebookUrl,
  builtByHubSpot,
  onTrackInteraction = () => {},
  phone,
  supportLanguages,
  supportUrl
}) {
  const {
    appContext,
    userInfo
  } = useDetailsPageContext();
  const isCustomer = appContext === 'app';
  const hasZorseWebticketScope = getUserHasZorseWebticketScope(userInfo);
  const hasZorseChatScope = getUserHasZorseChatScope(userInfo);
  const renderedEmailSupportLink = useMemo(() => {
    const emailSupportIconText = /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsx(IconHolder, {
        icon: EmailIcon
      }), /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.infoCard.contact.emailSupportLinkText"
      })]
    });
    if (builtByHubSpot && isCustomer && hasZorseWebticketScope) {
      return /*#__PURE__*/_jsx(LinkItem, {
        onClick: () => {
          onTrackInteraction({
            action: 'click copy email address'
          });
          trackCustomMarketplaceOfferingBehavioralEvent('SIDEBAR_EMAIL_SUPPORT');
          openHelpWidget({
            create: true
          });
        },
        children: emailSupportIconText
      });
    } else if (supportEmail) {
      return /*#__PURE__*/_jsx(CopyButton, {
        hint: unescapedText(`detailsPage.infoCard.contact.emailCopyButtonHint`),
        onClick: () => {
          onTrackInteraction({
            action: 'click copy email address'
          });
          trackCustomMarketplaceOfferingBehavioralEvent('SIDEBAR_EMAIL_SUPPORT');
        },
        value: supportEmail,
        use: "link",
        responsive: false,
        children: emailSupportIconText
      });
    }
    return null;
  }, [supportEmail, hasZorseWebticketScope, builtByHubSpot, isCustomer, onTrackInteraction]);
  const renderedLiveChatLink = useMemo(() => {
    const liveChatIconText = /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsx(IconHolder, {
        icon: MessagesIcon
      }), /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.infoCard.contact.liveChatLinkText"
      })]
    });
    if (builtByHubSpot && isCustomer && hasZorseChatScope) {
      return /*#__PURE__*/_jsx(LinkItem, {
        onClick: () => {
          onTrackInteraction({
            action: 'click live chat link'
          });
          trackCustomMarketplaceOfferingBehavioralEvent('SIDEBAR_LIVE_CHAT');
          openHelpWidget({
            chat: true
          });
        },
        children: liveChatIconText
      });
    } else if (liveChatUrl) {
      return /*#__PURE__*/_jsx(LinkItem, {
        href: getFormattedUrl(liveChatUrl),
        onClick: () => {
          onTrackInteraction({
            action: 'click live chat link'
          });
          trackCustomMarketplaceOfferingBehavioralEvent('SIDEBAR_LIVE_CHAT');
        },
        children: liveChatIconText
      });
    }
    return null;
  }, [liveChatUrl, hasZorseChatScope, builtByHubSpot, isCustomer, onTrackInteraction]);
  const renderedPhoneNumber = useMemo(() => {
    if (!phone) return null;
    const [phoneNumberBase, extension] = phone.split('-');
    const formattedPhoneNumberBase = /*#__PURE__*/_jsx(FormattedPhoneNumber, {
      value: phoneNumberBase
    });
    return extension ? /*#__PURE__*/_jsxs(LinkItem, {
      href: `tel:${phoneNumberBase}`,
      children: [/*#__PURE__*/_jsx(IconHolder, {
        icon: CallingIcon
      }), /*#__PURE__*/_jsx(FormattedReactMessage, {
        message: "detailsPage.infoCard.contact.phoneWithExtension",
        options: {
          phoneNumberBase: /*#__PURE__*/_jsx(Link, {
            href: `tel:${phoneNumberBase}`,
            children: formattedPhoneNumberBase
          }),
          extension
        }
      })]
    }) : /*#__PURE__*/_jsxs(Link, {
      href: `tel:${phoneNumberBase}`,
      children: [/*#__PURE__*/_jsx(IconHolder, {
        icon: CallingIcon
      }), formattedPhoneNumberBase]
    });
  }, [phone]);
  return /*#__PURE__*/_jsxs(Flex, {
    flexDirection: "column",
    alignItems: "start",
    gap: "100",
    children: [renderedEmailSupportLink, supportUrl && /*#__PURE__*/_jsxs(LinkItem, {
      href: getFormattedUrl(supportUrl),
      onClick: () => {
        onTrackInteraction({
          action: 'click support website link'
        });
        trackCustomMarketplaceOfferingBehavioralEvent('SIDEBAR_SUPPORT');
      },
      children: [/*#__PURE__*/_jsx(IconHolder, {
        icon: WebsiteIcon
      }), /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.infoCard.contact.supportWebsiteLinkText"
      })]
    }), renderedLiveChatLink, facebookUrl && /*#__PURE__*/_jsxs(LinkItem, {
      href: getFormattedUrl(facebookUrl),
      onClick: () => {
        onTrackInteraction({
          action: 'click facebook link'
        });
        trackCustomMarketplaceOfferingBehavioralEvent('SIDEBAR_FACEBOOK');
      },
      children: [/*#__PURE__*/_jsx(IconHolder, {
        icon: SocialBlockFacebookIcon
      }), /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.infoCard.contact.facebookLinkText"
      })]
    }), renderedPhoneNumber, supportLanguages && /*#__PURE__*/_jsxs(DescriptionList, {
      marginBlockStart: "400",
      children: [/*#__PURE__*/_jsx("dt", {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.infoCard.contact.supportLanguages"
        })
      }), /*#__PURE__*/_jsx("dd", {
        children: /*#__PURE__*/_jsx(Languages, {
          languages: supportLanguages
        })
      })]
    })]
  });
}